import { queryOptions } from "@tanstack/solid-query";
import { axiosClient } from "~/js/network/axiosClient";
import type { PiplineInfo } from "~/solidJs/entities/WorkspaceListEntity/helpers/workspaceTypes";
import { ApiErrorHandlers, BaseApi } from "~/solidJs/shared/api/baseApi";
import { queryClient } from "~/solidJs/shared/api/queryClient";
import type { IStartTrainingResponse, IStatusResponse } from "~/solidJs/shared/helpers/types";
import type {
	ICalculateCostDefinition,
	INodeStartTrainingSchema
} from "~/solidJs/shared/helpers/types/INodeDefinition";

interface ICostItem {
	cost: number;
	name: string;
	value: any;
}

export interface ICalculateCostResponse {
	price: number;
	details: {
		base_price: number;
		params: ICostItem[];
		inputs: ICostItem[];
	};
}

export interface ITaskLimit {
	node_id: number;
	id_global: string;
	limits_per_month: number | null;
	count_tasks: number;
}

export type IPostTask = (
	nodeData: INodeStartTrainingSchema,
	signal?: AbortSignal
) => Promise<IStartTrainingResponse>;

export type IGetTaskStatus = (taskId: number, signal?: AbortSignal) => Promise<IStatusResponse>;

export class TaskApi extends BaseApi {
	static baseUrlv1 = `${BaseApi.baseUrl}tasks/`;
	static baseUrl = `${BaseApi.baseUrl}v2/tasks/`;
	static statusUrl = `${this.baseUrl}queue-position/`;

	static getTaskLimitsOption() {
		return queryOptions({
			queryKey: ["taskLimits"],
			queryFn: () => TaskApi.getTasksLimits()
		});
	}
	static async getTasksLimits() {
		const { data } = await axiosClient.axiosClient.get<ITaskLimit[]>(
			`${TaskApi.baseUrlV2}/nodes/tariff_limits`
		);
		return data;
	}

	static async postTaskConnectedGraph(taskId: number, graph: PiplineInfo) {
		const { data } = await axiosClient.axiosClient.post<any>(
			`${TaskApi.baseUrlV2}/tasks/config_history`,
			{
				taskId,
				config: graph
			}
		);
		return data;
	}

	static async getTaskStatus(taskId: number, signal?: AbortSignal) {
		const { data } = await axiosClient.axiosClient.get<IStatusResponse>(
			TaskApi.statusUrl + taskId,
			{ signal }
		);
		return data;
	}

	static async postStartTraining(nodeData: INodeStartTrainingSchema, signal?: AbortSignal) {
		try {
			const { data } = await axiosClient.axiosClient.post<IStartTrainingResponse>(
				TaskApi.baseUrl,
				nodeData
			);
			if (signal?.aborted) {
				TaskApi.stopTask([{ task_id: data.task_id, status: "canceled" }]);
			}
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}
	static async postManualTask(taskPayload: {
		id_global: string;
		file_ids: number[];
		payload: any;
	}) {
		const { data } = await axiosClient.axiosClient.post<{ task_id: number }>(
			`${TaskApi.baseUrlv1}manual-task`,
			taskPayload
		);
		return data;
	}
	static async stopTask(
		taskPayload: {
			task_id: any;
			status: string;
		}[]
	) {
		const { data } = await axiosClient.axiosClient.post<{ task_id: number }>(
			`${TaskApi.baseUrl}task_status_list`,
			taskPayload
		);
		return data;
	}

	static getTaskCostOptions(input: ICalculateCostDefinition, signal?: AbortSignal) {
		return queryOptions({
			queryKey: ["getTaskCostOptions", input],
			queryFn: () => TaskApi.getTaskCost(input, signal),
			staleTime: 1000 * 60 * 2,
			gcTime: Infinity
		});
	}
	static async getCachedTaskCost(input: ICalculateCostDefinition, signal?: AbortSignal) {
		return queryClient.fetchQuery(TaskApi.getTaskCostOptions(input, signal));
	}
	static async getTaskCost(input: ICalculateCostDefinition, signal?: AbortSignal) {
		const { data } = await axiosClient.axiosClient.post<ICalculateCostResponse>(
			"api/v2/nodes/get_credits_price",
			input,
			{ signal }
		);
		return data;
	}
}

export class Sd3Api extends TaskApi {
	static baseUrl = `${TaskApi.baseUrl}sd3tti`;
	static statusUrl = `${Sd3Api.baseUrl}/`;
}
export class SdVideoApi extends TaskApi {
	static baseUrl = `${TaskApi.baseUrl}sd3itv`;
	static statusUrl = `${SdVideoApi.baseUrl}/`;
}
export class CraftsmanApi extends TaskApi {
	static baseUrl = `${TaskApi.baseUrl}craftsman`;
	static statusUrl = `${CraftsmanApi.baseUrl}/`;
}
export class ICLightApi extends TaskApi {
	static baseUrl = `${TaskApi.baseUrl}iclight`;
	static statusUrl = `${ICLightApi.baseUrl}/`;
}
export class DalleApi extends TaskApi {
	static baseUrl = `${TaskApi.baseUrl}v2/dalle3`;
	static statusUrl = `${DalleApi.baseUrl}/`;
}

export class RunWayApi extends TaskApi {
	static baseUrl = `${TaskApi.baseUrl}runway`;
}

export class MidjourneyApi extends TaskApi {
	static baseUrl = `${TaskApi.baseUrl}midjourney`;
}

export class MidjourneyVariateApi extends MidjourneyApi {
	static async postMjStartTraining(
		nodeData: INodeStartTrainingSchema,
		action: "variate" | "upscale" | "pan" | "zoomout",
		signal?: AbortSignal
	) {
		try {
			const { data } = await axiosClient.axiosClient.post<IStartTrainingResponse>(
				MidjourneyVariateApi.baseUrl,
				nodeData,
				{ params: { action } }
			);
			if (signal?.aborted) {
				TaskApi.stopTask([{ task_id: data.task_id, status: "canceled" }]);
			}
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
			throw e;
		}
	}
}
